import { fetchAndSetFounderDocuments } from 'components/views/FounderVault/_helpers/founderVault.resolvers';
import { $user, $form, $view } from 'signals/global.signals';
import displayError from 'utils/displayError';
import { updateDocument, createDocument, deleteDocument, uploadDocumentToStorage } from 'api/documents.api';
import getSelectValue from 'utils/getSelectValue';
import displayNotification from 'utils/displayNotification';
import { createFolder, deleteFolder, updateFolder } from 'api/folders.api';
import { updateOrgFinancialMetrics } from 'api/metrics.api';

// Document CRUD Operations
export const handleSubmitDocumentCreateSubmit = async () => {
  $view.update({ isModalLoading: true });
  try {
    let folderId = getSelectValue($form.value.folder);
    const isNewFolder = !$view.value.folderOptions.find(option => option.value === folderId);

    if (isNewFolder) {
      const folder = await createFolder({
        name: getSelectValue($form.value.folder),
      });
      folderId = folder.id;
    }
    await createDocument({
      name: $form.value.name,
      folderId,
      organizationId: $user.value.organization.id,
      url: $form.value.fileUrl || '',
    });
    await fetchAndSetFounderDocuments();
    $view.update({ expandedFolders: { [folderId]: true } });
  } catch (error) {
    displayError({
      message: 'Failed to create document. Please try again.',
      title: 'Document Creation Error',
    });
  } finally {
    $view.update({ isModalLoading: false, activeModal: null });
  }
};

export const handleSubmitDocumentEditSubmit = async () => {
  $view.update({ isModalLoading: true });
  try {
    let folderId = getSelectValue($form.value.folder);
    const isNewFolder = !$view.value.folderOptions.find(option => option.value === folderId);
    if (isNewFolder) {
      const folder = await createFolder({
        name: folderId,
      });
      folderId = folder.id;
    }

    await updateDocument($form.value.id, {
      name: $form.value.name,
      folderId,
    });

    displayNotification({ message: 'Document updated successfully' });
  } catch (error) {
    displayError({
      message: 'Failed to update document. Please try again.',
      title: 'Document Update Error',
    });
  } finally {
    $view.update({ isModalLoading: false, activeModal: null });
    await fetchAndSetFounderDocuments();
  }
};

export const handleSubmitDocumentDeleteSubmit = async () => {
  $view.update({ isModalLoading: true });
  try {
    await deleteDocument({
      id: $form.value.id,
    });
    displayNotification({ message: 'Document deleted successfully' });
  } catch (error) {
    displayError({
      message: 'Failed to delete document. Please try again.',
      title: 'Document Deletion Error',
    });
  } finally {
    $view.update({ isModalLoading: false, activeModal: null });
    await fetchAndSetFounderDocuments();
  }
};

export const handleChangeFounderDocumentFileInput = async (e) => {
  try {
    const file = e.target.files[0];
    const url = await uploadDocumentToStorage({
      type: 'organization',
      file,
      itemId: $user.value.organization.id,
    });
    $form.update({
      fileUrl: url,
    });
  } catch (error) {
    displayError({
      message: 'Failed to upload file. Please try again.',
      title: 'File Upload Error',
    });
  }
};

// Folder CRUD Operations
export const handleSubmitFolderEdit = async () => {
  $view.update({ isModalLoading: true });
  try {
    await updateFolder($form.value.id, {
      name: $form.value.name,
    });
    displayNotification({ message: 'Folder updated successfully' });
  } catch (error) {
    displayError({
      message: 'Failed to update folder. Please try again.',
      title: 'Folder Update Error',
    });
  } finally {
    $view.update({ isModalLoading: false, activeModal: null });
    await fetchAndSetFounderDocuments();
  }
};

export const handleSubmitFolderDelete = async () => {
  $view.update({ isModalLoading: true });
  try {
    await deleteFolder($form.value.id);
    displayNotification({ message: 'Folder deleted successfully' });
  } catch (error) {
    displayError({
      message: 'Failed to delete folder. Please try again.',
      title: 'Folder Deletion Error',
    });
  } finally {
    $view.update({ isModalLoading: false, activeModal: null });
    await fetchAndSetFounderDocuments();
  }
};

// UI Interactions
export const handleDocumentView = (document) => {
  window.open(document.url, '_blank');
};

export const handleSubmitFinancialsEdit = async () => {
  $view.update({ isLoading: true });

  try {
    // Define required fields clearly
    const requiredFields = [
      { field: 'otherAssets', label: 'Other Assets' },
      { field: 'investments', label: 'Investments' },
      { field: 'liabilities', label: 'Liabilities' },
      { field: 'amountPreviouslyRaised', label: 'Amount Previously Raised' },
    ];

    // Check for missing required fields
    const missingFields = requiredFields.filter(({ field }) => {
      const value = $form.value[field];
      return value === null || value === undefined || value === '';
    });

    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(({ label }) => label).join(', ');
      displayError({
        message: `Please fill in all required fields: ${fieldNames}`,
        title: 'Missing Fields',
      });
      return;
    }

    // Validate numeric fields
    const numericFields = ['postMoneyValuation', 'amountPreviouslyRaised', 'arr', 'mrr', 'otherRevenue', 'cashOnHand', 'liabilities', 'investments', 'otherAssets'];
    const invalidFields = numericFields.filter(field => {
      const value = $form.value[field];
      return value !== null && value !== undefined && value !== '' && (Number.isNaN(value) || value < 0);
    });

    if (invalidFields.length > 0) {
      displayError({
        message: `Please enter valid positive numbers for: ${invalidFields.join(', ')}`,
        title: 'Invalid Values',
      });
      return;
    }

    // Convert string values to numbers where needed
    const financialData = {
      postMoneyValuation: parseFloat($form.value.postMoneyValuation) || null,
      amountPreviouslyRaised: parseFloat($form.value.amountPreviouslyRaised),
      arr: parseFloat($form.value.arr) || null,
      mrr: parseFloat($form.value.mrr) || null,
      otherRevenue: parseFloat($form.value.otherRevenue) || null,
      cashOnHand: parseFloat($form.value.cashOnHand) || null,
      liabilities: parseFloat($form.value.liabilities),
      investments: parseFloat($form.value.investments),
      otherAssets: parseFloat($form.value.otherAssets),
    };

    await updateOrgFinancialMetrics(financialData);
    displayNotification({ message: 'Financials updated successfully' });
  } catch (error) {
    displayError({
      message: 'Failed to update financial data. Please check your information and try again.',
      title: 'Financial Update Error',
    });
  } finally {
    $view.update({ isLoading: false });
  }
};

export const handleCreateFolder = async () => {
  $view.update({ isModalLoading: true });
  try {
    const folder = await createFolder({
      name: $form.value.name,
    });
    displayNotification({ message: 'Folder created successfully' });
    $view.update({
      expandedFolders: { [folder.id]: true },
      activeModal: null,
    });
    await fetchAndSetFounderDocuments();
  } catch (error) {
    displayError(error);
  } finally {
    $view.update({ isModalLoading: false });
  }
};
